import React, { useCallback, useEffect, useState } from 'react';
import { Cpu, MemoryStick, Gauge as GaugeIcon, Zap, Timer, Globe } from 'lucide-react';
import { adminApi as api } from '@/lib/api';
import { useLiveChannel } from '@/lib/live';
import DashboardShell from '@/components/enterprise/DashboardShell';
import { Panel, Kpi, StatusPill, Bar, ConnectionState } from '@/components/enterprise/ui';
import { AreaChart } from '@/components/enterprise/charts';

const cap = (arr, v, n = 40) => [...arr, v].slice(-n);

export default function MonitoringDashboard() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState({ cpu: [], mem: [], net_in: [], net_out: [], latency: [] });
  const [kpis, setKpis] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/dashboards/monitoring').then((r) => {
      setSnap(r.data);
      if (r.data.status === 'connected' && r.data.data) {
        setSeries(r.data.data.charts);
        setKpis(r.data.data.kpis);
      }
    }).catch(() => setSnap({ status: 'offline', source: 'monitoring' })).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onTick = useCallback((msg) => {
    if (!msg || !msg.data) return;
    if (msg.data.kpis) setKpis(msg.data.kpis);
    const p = msg.data.point;
    if (p) setSeries((s) => ({
      cpu: cap(s.cpu, p.cpu), mem: cap(s.mem, p.mem),
      net_in: cap(s.net_in, p.net_in), net_out: cap(s.net_out, p.net_out), latency: cap(s.latency, p.latency),
    }));
  }, []);
  useLiveChannel('monitoring', onTick);

  const ok = snap && snap.status === 'connected' && snap.data;
  const d = ok ? snap.data : null;
  const k = kpis || (d && d.kpis);

  return (
    <DashboardShell title="Enterprise Monitoring Center" subtitle={ok ? `prometheus / grafana · source: ${snap.source}` : 'observability'}>
      {loading && <div className="text-sm text-[color:var(--itw-text-3)] font-mono">loading metrics…</div>}
      {!loading && !ok && <Panel><ConnectionState status={snap ? snap.status : 'offline'} source={snap ? snap.source : 'monitoring'} onRetry={load} /></Panel>}
      {ok && k && (
        <div className="space-y-6" data-testid="monitoring-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi label="CPU" value={k.cpu} unit="%" testid="mon-kpi-cpu" />
            <Kpi label="Memory" value={k.mem} unit="%" accent="#5AF3FF" />
            <Kpi label="Latency" value={k.latency_ms} unit="ms" testid="mon-kpi-latency" />
            <Kpi label="Requests/s" value={k.requests_s.toLocaleString('de-DE')} />
            <Kpi label="Bandwidth" value={k.bandwidth_gbps} unit="Gbps" />
            <Kpi label="Uptime" value={k.uptime_pct} unit="%" accent="#2EF2A5" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Network Throughput" subtitle="MB/s in/out" status="connected" testid="mon-net-chart">
              <AreaChart data={series.net_in} color="#00E7FF" height={130} />
              <AreaChart data={series.net_out} color="#5AF3FF" height={130} />
            </Panel>
            <Panel title="CPU & Latency" subtitle="rolling 40 samples" status="connected" testid="mon-cpu-chart">
              <AreaChart data={series.cpu} color="#2EF2A5" height={130} />
              <AreaChart data={series.latency} color="#FFCC66" height={130} />
            </Panel>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Panel title="Hosts" subtitle="node-exporter" className="lg:col-span-2" testid="mon-hosts">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono text-left"><th className="py-2 font-normal">Host</th><th className="py-2 font-normal">CPU</th><th className="py-2 font-normal">MEM</th><th className="py-2 font-normal">Disk</th><th className="py-2 font-normal">Status</th></tr></thead>
                <tbody className="font-mono text-[13px]">{d.hosts.map((h) => (
                  <tr key={h.host} className="border-t border-[color:var(--itw-border)]">
                    <td className="py-2 text-[color:var(--itw-text)]">{h.host}</td>
                    <td className="py-2 w-24"><Bar value={h.cpu} max={100} height={5} /></td>
                    <td className="py-2 w-24"><Bar value={h.mem} max={100} height={5} color="#5AF3FF" /></td>
                    <td className="py-2 w-24"><Bar value={h.disk} max={100} height={5} color={h.disk > 70 ? '#FFCC66' : '#2EF2A5'} /></td>
                    <td className="py-2"><StatusPill status={h.status} /></td>
                  </tr>
                ))}</tbody>
              </table></div>
            </Panel>
            <Panel title="Active Alerts" subtitle="alertmanager" testid="mon-alerts">
              <div className="space-y-2">{d.alerts.map((a) => (
                <div key={a.id} className="rounded-lg border border-[color:var(--itw-border)] px-3 py-2">
                  <div className="flex items-center justify-between"><StatusPill status={a.severity} /><span className="text-[10px] font-mono text-[color:var(--itw-text-3)]">{a.since}</span></div>
                  <div className="mt-1 text-[12px] text-[color:var(--itw-text-2)]">{a.summary}</div>
                </div>
              ))}</div>
            </Panel>
          </div>

          <Panel title="Uptime Monitors" subtitle="synthetic checks" testid="mon-uptime">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{d.uptime.map((u) => (
              <div key={u.name} className="rounded-lg border border-[color:var(--itw-border)] p-3">
                <div className="flex items-center justify-between"><span className="text-[12px] text-[color:var(--itw-text)] truncate">{u.name}</span><StatusPill status={u.status} /></div>
                <div className="mt-2 flex items-baseline justify-between font-mono"><span className="text-lg text-[color:var(--itw-cyan)]">{u.uptime}%</span><span className="text-[11px] text-[color:var(--itw-text-3)]">{u.ms}ms</span></div>
              </div>
            ))}</div>
          </Panel>
        </div>
      )}
    </DashboardShell>
  );
}
